import { LayaGL } from "../../../../layagl/LayaGL";
import { DrawType } from "../../../../RenderEngine/RenderEnum/DrawType";
import { IndexFormat } from "../../../../RenderEngine/RenderEnum/IndexFormat";
import { MeshTopology } from "../../../../RenderEngine/RenderEnum/RenderPologyMode";
import { Pool } from "../../../../utils/Pool";
import { FastSinglelist } from "../../../../utils/SingletonList";
import { BufferUsage } from "../../../../RenderEngine/RenderEnum/BufferTargetType";
;
import { Web2DGraphicsIndexBatchBuffer } from "./Web2DGraphic2DBuffer";
import { WebRender2DPass } from "./WebRender2DPass";
import { ShaderDefines2D } from "../../../../webgl/shader/d2/ShaderDefines2D";
class BatchBuffer {
    constructor() {
        this.indexCount = 0;
        this.maxIndexCount = 0;
        this.bufferStates = new Map();
        this.indexBuffer = LayaGL.renderDeviceFactory.createIndexBuffer(BufferUsage.Dynamic);
        this.indexBuffer.indexType = IndexFormat.UInt16;
        this.wholeBuffer = new Web2DGraphicsIndexBatchBuffer();
        this.wholeBuffer.buffer = this.indexBuffer;
    }
    add(element) {
        let handle = element.owner.renderDataHandler;
        let blocks = handle._getBlocks();
        if (!blocks)
            return null;
        let cview = handle.getCloneViews()[element._index];
        let block = blocks[element._index];
        let vertexBuffer = block.vertexBuffer;
        let bufferState = this.bindBuffer(vertexBuffer);
        this.indexCount += cview.length;
        this.wholeBuffer._modifyOneView(cview);
        if (cview._geometry.bufferState !== bufferState) {
            cview._geometry.bufferState = bufferState;
        }
        WebRender2DPass.setBuffer(this.wholeBuffer);
        this.updateBufLength();
        return cview._geometry;
    }
    updateBufLength() {
        if (this.maxIndexCount <= this.indexCount) {
            let nLength = Math.ceil(this.indexCount / _STEP_) * _STEP_;
            let byteLength = nLength * 2;
            this.indexBuffer._setIndexDataLength(byteLength);
            this.wholeBuffer._resetData(byteLength);
            this.maxIndexCount = nLength;
        }
    }
    bindBuffer(buffer) {
        let bufferState = this.bufferStates.get(buffer);
        if (!bufferState) {
            bufferState = LayaGL.renderDeviceFactory.createBufferState();
            bufferState.applyState([buffer], this.indexBuffer);
            this.bufferStates.set(buffer, bufferState);
        }
        return bufferState;
    }
    clear() {
        this.indexCount = 0;
        this.wholeBuffer.clearBufferViews();
    }
    destroy() {
        this.clear();
        this.bufferStates.forEach((bufferState) => {
            bufferState.destroy();
        });
        this.bufferStates.clear();
        this.indexBuffer.destroy();
        this.indexBuffer = null;
        this.wholeBuffer.destroy();
        this.wholeBuffer = null;
    }
}
class BatchContext {
    constructor() {
        this.textureId = 0;
        this.globalAlpha = 1;
        this.clipInfo = null;
        this.subShader = null;
        this.bufferState = null;
        this.primitiveShaderData = null;
        this.materialShaderData = null;
        this.type = 0;
        this.lowType = 0;
        this.globalRenderData = null;
    }
    setHead(element) {
        this.textureId = element.type & (~63);
        this.primitiveShaderData = element.primitiveShaderData;
        this.materialShaderData = element.materialShaderData;
        this.globalAlpha = element.owner.globalAlpha;
        this.clipInfo = element.owner.getClipInfo();
        this.subShader = element.subShader;
        this.bufferState = element.geometry.bufferState;
        this.type = element.type;
        this.lowType = element.type & 63;
        this.globalRenderData = element.owner.globalRenderData;
    }
    isCompatible(element) {
        if (this.type & 32)
            return false;
        let elementType = element.type;
        if (elementType & 32) {
            return false;
        }
        let elementLowType = elementType & 63;
        let elementTexId = elementType & (~63);
        let elementOwner = element.owner;
        if (element.primitiveShaderData.getVector(ShaderDefines2D.UNIFORM_TEXRANGE)) {
            return false;
        }
        if (this.lowType !== elementLowType) {
            return false;
        }
        if (this.lowType & 16 && element.materialShaderData !== this.materialShaderData) {
            return false;
        }
        if (this.globalAlpha !== elementOwner.globalAlpha) {
            return false;
        }
        if (this.subShader !== element.subShader ||
            this.bufferState !== element.geometry.bufferState ||
            this.clipInfo !== elementOwner.getClipInfo() ||
            elementOwner.globalRenderData !== this.globalRenderData) {
            return false;
        }
        if (this.textureId === 0) {
            if (elementTexId !== 0) {
                this.textureId = elementTexId;
                this.primitiveShaderData = element.primitiveShaderData;
            }
            return true;
        }
        return elementTexId === 0 || elementTexId === this.textureId;
    }
}
export class WebGraphicsBatch {
    constructor() {
        this._buffer = new BatchBuffer();
        this._merged = [];
        this._context = new BatchContext();
    }
    reset() {
        this._buffer.clear();
        WebGraphicsBatch._pool.recover(this._merged);
    }
    destroy() {
        this._buffer.destroy();
        WebGraphicsBatch._pool.recover(this._merged);
    }
    batch(list, start, end, allowReorder) {
        let elementArray = list.elements;
        let ctx = this._context;
        ctx.setHead(elementArray[start]);
        let batchStart = start;
        for (let i = start + 1; i <= end; i++) {
            let element = elementArray[i];
            if (ctx.isCompatible(element))
                continue;
            if (allowReorder) {
                for (let j = i + 1; j <= end; j++) {
                    let element2 = elementArray[j];
                    if (ctx.isCompatible(element2)) {
                        for (let k = j - 1; k >= i; k--) {
                            if (element2.owner.dcBounds.intersects(elementArray[k].owner.dcBounds)) {
                                element2 = null;
                                break;
                            }
                        }
                        if (element2 != null) {
                            elementArray.splice(j, 1);
                            elementArray.splice(i, 0, element2);
                            element = elementArray[++i];
                        }
                    }
                }
            }
            if (i - batchStart > 1)
                this.merge(list, batchStart, i - 1, ctx);
            else
                this.addSingle(list, elementArray[batchStart]);
            batchStart = i;
            ctx.setHead(element);
        }
        if (end - batchStart > 0)
            this.merge(list, batchStart, end, ctx);
        else
            this.addSingle(list, elementArray[batchStart]);
    }
    addSingle(list, element) {
        this._buffer.add(element);
        list.add(element);
    }
    merge(list, start, end, batchContext) {
        let elementArray = list.elements;
        let staticBatchRenderElement = WebGraphicsBatch._pool.take();
        this._merged.push(staticBatchRenderElement);
        let drawArray = [];
        let drawLengths = [];
        for (let i = start; i <= end; i++) {
            let element = elementArray[i];
            let geometry = this._buffer.add(element) || element.geometry;
            if (i === start) {
                staticBatchRenderElement.geometry.bufferState = geometry.bufferState;
                staticBatchRenderElement.materialShaderData = element.materialShaderData;
                staticBatchRenderElement.value2DShaderData = element.value2DShaderData;
                staticBatchRenderElement.subShader = element.subShader;
                staticBatchRenderElement.renderStateIsBySprite = element.renderStateIsBySprite;
                staticBatchRenderElement.primitiveShaderData = batchContext.primitiveShaderData;
                staticBatchRenderElement.owner = element.owner;
            }
            geometry.getDrawDataParams(TEMP_SINGLE_LIST);
            drawArray.push(TEMP_SINGLE_LIST.elements.slice());
            drawLengths.push(TEMP_SINGLE_LIST.length);
        }
        let geometry = staticBatchRenderElement.geometry;
        let len = drawArray.length;
        let currentOffset = 0;
        let currentCount = 0;
        let isFirst = true;
        for (let i = 0; i < len; i++) {
            let drawParam = drawArray[i];
            let drawLength = drawLengths[i];
            for (let j = 0; j < drawLength; j += 2) {
                let offset = drawParam[j];
                let count = drawParam[j + 1];
                if (isFirst) {
                    currentOffset = offset;
                    currentCount = count;
                    isFirst = false;
                    continue;
                }
                if (currentOffset + currentCount * 2 === offset) {
                    currentCount += count;
                }
                else {
                    geometry.setDrawElemenParams(currentCount, currentOffset);
                    currentOffset = offset;
                    currentCount = count;
                }
            }
        }
        if (!isFirst) {
            geometry.setDrawElemenParams(currentCount, currentOffset);
        }
        list.add(staticBatchRenderElement);
    }
}
WebGraphicsBatch._pool = Pool.createPool2(() => {
    let element = LayaGL.render2DRenderPassFactory.createPrimitiveRenderElement2D();
    element.geometry = LayaGL.renderDeviceFactory.createRenderGeometryElement(MeshTopology.Triangles, DrawType.DrawElement);
    element.geometry.indexFormat = IndexFormat.UInt16;
    element.nodeCommonMap = ["Sprite2D"];
    element.renderStateIsBySprite = false;
    return element;
}, null, element => {
    element.geometry.clearRenderParams();
    element.geometry.bufferState = null;
    element.materialShaderData = null;
    element.value2DShaderData = null;
    element.primitiveShaderData = null;
    element.subShader = null;
    element.owner = null;
    element.renderStateIsBySprite = false;
    element.globalShaderData = null;
});
const TEMP_SINGLE_LIST = new FastSinglelist();
const _STEP_ = 1024;
